import { useCallback, useState } from 'react';
import type { Ticket } from './mock';

/**
 * Macros / canned responses — reusable reply templates with {variables}, plus
 * an optional status to apply. Built-ins + org custom (localStorage now →
 * a per-tenant `macros` table live).
 */
export type Macro = {
  id: string;
  name: string;
  body: string;
  setStatus?: string;
  custom?: boolean;
};

const DEFAULT_MACROS: Macro[] = [
  { id: 'ack', name: 'Acknowledge', body: 'Thanks for reaching out — we’ve received {ref} and are looking into it now.' },
  { id: 'info', name: 'Request more info', body: 'Could you share a bit more detail (screenshots, steps to reproduce, affected user)? We’ll proceed as soon as we have it.' },
  { id: 'working', name: 'In progress', body: 'We’re actively working on {ref} and will update you shortly.' },
  { id: 'resolved', name: 'Mark resolved', body: 'We’ve resolved {ref}. Please reopen or reply if anything else comes up.', setStatus: 'Resolved' },
  { id: 'escalate', name: 'Escalating', body: 'Escalating {ref} to the next tier for faster resolution.' },
];

const LS_KEY = 'compass-macros';

function loadCustom(): Macro[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
}

function newId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `m-${Date.now()}`;
}

/** Substitute {ref}, {requester}, {assignee} placeholders. */
export function applyVars(body: string, t?: Ticket): string {
  if (!t) return body;
  return body
    .replace(/\{ref\}/g, t.ref)
    .replace(/\{requester\}/g, t.requester)
    .replace(/\{assignee\}/g, t.assignee ?? 'the team');
}

export function useMacros() {
  const [custom, setCustom] = useState<Macro[]>(() => loadCustom());

  const persist = useCallback((next: Macro[]) => {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    setCustom(next);
  }, []);

  const macros = [...DEFAULT_MACROS, ...custom];

  const add = useCallback(
    (input: { name: string; body: string; setStatus?: string }) => {
      persist([...loadCustom(), { ...input, id: newId(), custom: true }]);
    },
    [persist],
  );

  const remove = useCallback(
    (id: string) => {
      persist(loadCustom().filter((m) => m.id !== id));
    },
    [persist],
  );

  return { macros, add, remove };
}
